<script setup>
  import { computed } from "vue";
  import { useChartPreview } from "../composables/useChartPreview";

  const props = defineProps({
    colors: { type: Array, required: true },
    width: { type: Number, required: true },
    height: { type: Number, required: true },
    padding: { type: Number, required: true },
    seriesCount: { type: Number, required: true },
    categoryCount: { type: Number, required: true },
  });

  const { chartWidth, chartHeight, displayColors } = useChartPreview(props);

  const stackedBarGap = 8;

  const stackedBarHeight = computed(() => {
    const calculated = (chartHeight.value - (props.categoryCount - 1) * stackedBarGap) / props.categoryCount;
    const maxHeight = chartHeight.value * 0.2;
    return Math.min(calculated, maxHeight);
  });

  const stackedBarOffsetY = computed(() => {
    const totalBarsHeight =
      props.categoryCount * stackedBarHeight.value + (props.categoryCount - 1) * stackedBarGap;
    return (chartHeight.value - totalBarsHeight) / 2;
  });

  const stackedSegments = computed(() => {
    const segments = [];
    let offset = 0;
    for (let i = 0; i < props.seriesCount; i++) {
      const baseWidth = 1 / props.seriesCount;
      const variance = (i % 2 === 0 ? 0.05 : -0.05) * (i < props.seriesCount - 1 ? 1 : 0);
      const width = baseWidth + variance;
      segments.push({ offset, width });
      offset += width;
    }
    const total = segments.reduce((sum, s) => sum + s.width, 0);
    segments.forEach((s) => (s.width = s.width / total));
    let cumOffset = 0;
    segments.forEach((s) => {
      s.offset = cumOffset;
      cumOffset += s.width;
    });
    return segments;
  });
</script>

<template>
  <g :transform="`translate(0, ${stackedBarOffsetY})`">
    <g
      v-for="(cat, catIdx) in categoryCount"
      :key="catIdx">
      <rect
        v-for="(segment, segIdx) in stackedSegments"
        :key="segIdx"
        :x="padding + segment.offset * chartWidth"
        :y="padding + catIdx * (stackedBarHeight + stackedBarGap)"
        :width="segment.width * chartWidth"
        :height="stackedBarHeight"
        :fill="displayColors[segIdx % displayColors.length]" />
    </g>
  </g>
</template>
